import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
import { getWorkItem } from '../data/work';
import { FrameViewer } from '../components/FrameViewer';
import { ModelStage } from '../components/ModelStage';
import { PromptCard } from '../components/PromptCard';
import { demos } from '../components/demos';
import type { WorkBlock, WorkFigure } from '../types/work';
const ease = [0.23, 1, 0.32, 1] as const;
const aspects: Record<string, string> = {
  '16/9': 'aspect-video',
  '3/2': 'aspect-[3/2]',
  '4/3': 'aspect-[4/3]',
  auto: ''
};
const pending = 'flex flex-col items-start justify-end border border-dashed border-rule bg-surface/60 p-6';

/**
 * Emphasis: `_like this_` or `*like this*`.
 *
 * Bounded by a single delimiter pair, so a stray underscore inside an
 * identifier cannot start one — though `a_b_c` in body copy would still be read
 * as emphasis. That has not come up, and the alternative is a lookbehind that
 * costs more to read than the bug it prevents.
 */
function Emphasis({
  text
}: {
  text: string;
}) {
  const parts = text.split(/(\*[^*]+\*|_[^_]+_)/g);
  return <>
      {parts.map((part, index) => {
      const marked = part.length > 2 && (part.startsWith('*') && part.endsWith('*') || part.startsWith('_') && part.endsWith('_'));
      if (!marked) return <React.Fragment key={index} data-id="element-106" data-mp="2lppof4vu8pj5">{part}</React.Fragment>;
      return <em key={index} data-id="element-107" data-mp="2ppgbaxi8ulie" data-mpi={index}>{part.slice(1, -1)}</em>;
    })}
    </>;
}

/**
 * Inline markup in body copy: links, then emphasis inside whatever is left.
 *
 * Prose is stored as plain strings so the data file stays readable, but a
 * write-up that cannot cite its own sources is not much of a write-up, and one
 * that cannot italicise a word loses the distinction it was drawing. Two passes
 * rather than one combined pattern — these do not nest, and a single regex for
 * both is far harder to read than it is worth.
 */
function Rich({
  text
}: {
  text: string;
}) {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\))/g);
  return <>
      {parts.map((part, index) => {
      const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (!link) return <Emphasis key={index} text={part} data-id="element-108" data-mp="3vclbgwimmmte" />;
      return <a key={index} href={link[2]} target="_blank" rel="noreferrer" className="border-b border-rule text-ink transition-colors duration-150 ease-expo hover:border-ink" data-id="element-109" data-mp="3qfbg708k3gxn" data-mpi={index}>
            {link[1]}
          </a>;
    })}
    </>;
}
function Figure({
  figure
}: {
  figure: WorkFigure;
}) {
  // An asset whose URL is not filled in yet says so, rather than rendering as a
  // broken image. Same job the standalone `placeholder` block does, but inline,
  // so the alt text and caption that will ship alongside the image are already
  // written and reviewable — and turning it on is one URL, not an edit.
  if (!figure.src) {
    return <figure data-id="element-110" data-mp="ugap9k2zor08">
        {/* `auto` means “let the image decide”, which for a box with no image in
            it means no height at all. */}
        <div className={`${pending} ${aspects[figure.aspect === 'auto' ? '3/2' : figure.aspect ?? '3/2']}`} data-id="element-111" data-mp="kiwiydas5iln">
          <p className="text-[15px] font-medium text-ink-muted" data-id="element-112" data-mp="ph7sso12rt3v">Image pending upload</p>
          <p className="mt-2 text-[13px] leading-relaxed text-ink-faint" data-id="element-113" data-mp="1d724315qx9m0">{figure.alt}</p>
        </div>
        {figure.caption && <figcaption className="mt-3 text-[13px] leading-relaxed text-ink-faint" data-id="element-114" data-mp="1i5c1liey0mas">
            {figure.caption}
          </figcaption>}
      </figure>;
  }
  return <figure data-id="element-115" data-mp="187pjkn2ckeml">
      <div className="overflow-hidden bg-surface" data-id="element-116" data-mp="187fnqiumfd8z">
        <img src={figure.src} alt={figure.alt} className={`w-full object-cover ${aspects[figure.aspect ?? '3/2']} ${figure.tone === 'full' ? '' : 'grayscale'}`} data-id="element-117" data-mp="3ox4f6lq8zdih" />
      </div>
      {figure.caption && <figcaption className="mt-3 text-[13px] leading-relaxed text-ink-faint" data-id="element-118" data-mp="d21aehgcn6ie">
          {figure.caption}
        </figcaption>}
    </figure>;
}

/**
 * A section heading, sized as one.
 *
 * These were 13px — smaller than the body copy they introduced, which made them
 * read as captions and left the page with no visible structure between the title
 * and the paragraphs.
 */
function Heading({
  children
}: {
  children: React.ReactNode;
}) {
  return <h2 className="text-[26px] font-medium leading-tight tracking-tight text-ink md:text-[30px]" data-id="element-119" data-mp="qzya3mq3nt1a">
      {children}
    </h2>;
}
function Block({
  block
}: {
  block: WorkBlock;
}) {
  switch (block.kind) {
    case 'prose':
      return <>
          {block.heading && <Heading data-id="element-120" data-mp="36rdtdo162c7u">{block.heading}</Heading>}
          <div className={`space-y-5 text-[17px] leading-relaxed text-ink-muted ${block.heading ? 'mt-5' : ''}`} data-id="element-121" data-mp="36s5cw0hhojhr">
            {block.body.map((paragraph, index) => <p key={index} data-id="element-122" data-mp="1i9kq31vocf4d" data-mpi={index}>
                <Rich text={paragraph} data-id="element-123" data-mp="1m9bckte64933" />
              </p>)}
          </div>
        </>;
    case 'list':
      return <>
          {block.heading && <Heading data-id="element-124" data-mp="1r73fsjk9jhml">{block.heading}</Heading>}
          {block.body && <div className={`space-y-5 text-[17px] leading-relaxed text-ink-muted ${block.heading ? 'mt-5' : ''}`} data-id="element-125" data-mp="1w0670l1kiezm">
              {block.body.map((paragraph, index) => <p key={index} data-id="element-126" data-mp="25zf1rg49bxn1" data-mpi={index}>
                  <Rich text={paragraph} data-id="element-127" data-mp="29z5p19ukrvmt" />
                </p>)}
            </div>}
          <ul className={block.heading || block.body ? 'mt-6' : ''} data-id="element-128" data-mp="2eyil8l7c6f22">
            {block.items.map((item, index) => <li key={index} className="flex gap-3 border-b border-rule py-3 text-[17px] leading-relaxed text-ink-muted first:border-t" data-id="element-129" data-mp="flxy3t9q046g" data-mpi={index}>
                <span className="select-none pt-0.5 text-ink-faint" aria-hidden="true" data-id="element-130" data-mp="1nu3k57ycr7tf">
                  —
                </span>
                <span className="min-w-0" data-id="element-131" data-mp="1ixd4glqgme8j">
                  <Rich text={item} data-id="element-132" data-mp="1wvjs791p2xuh" />
                </span>
              </li>)}
          </ul>
          {block.caption && <p className="mt-4 text-[13px] leading-relaxed text-ink-faint" data-id="element-133" data-mp="1rvfaxgsypz37">{block.caption}</p>}
        </>;
    case 'prompt':
      return <div className="border-l border-rule pl-6" data-id="element-134" data-mp="21uo2k30cv1me">
          {block.heading && <h2 className="text-[19px] font-medium leading-snug tracking-tight text-ink" data-id="element-135" data-mp="21stsjjqmgtx3">
              {block.heading}
            </h2>}
          {block.body && <div className={`space-y-3 text-[16px] leading-relaxed text-ink-muted ${block.heading ? 'mt-3' : ''}`} data-id="element-136" data-mp="2bl9baj9qtmj1">
              {block.body.map((paragraph, index) => <p key={index} data-id="element-137" data-mp="346xx3m41c2if" data-mpi={index}>
                  <Rich text={paragraph} data-id="element-138" data-mp="346o6bwb61rbo" />
                </p>)}
            </div>}
          <div className={block.heading || block.body ? 'mt-5' : ''} data-id="element-139" data-mp="394zhcad767vh">
            <PromptCard text={block.text} label={block.label} note={block.note} data-id="element-140" data-mp="2u9082wecq1or" />
          </div>
        </div>;
    case 'figure':
      return <>
          {block.heading && <Heading data-id="element-141" data-mp="2lgg7vrievp1t">{block.heading}</Heading>}
          <div className={block.heading ? 'mt-5' : ''} data-id="element-142" data-mp="2lfetpe63mmfv">
            <Figure figure={block.figure} data-id="element-143" data-mp="2q76d94qu7rwi" />
          </div>
        </>;
    case 'compare':
      return <>
          {block.heading && <Heading data-id="element-144" data-mp="2bjla5atypsol">{block.heading}</Heading>}
          <div className={`grid gap-4 sm:grid-cols-2 ${block.heading ? 'mt-5' : ''}`} data-id="element-145" data-mp="2bgfl1vlc2qsv">
            {block.figures.map(figure => <Figure key={figure.src} figure={figure} data-id="element-146" data-mp="22hbzmkyp1zb7" />)}
          </div>
          {block.caption && <p className="mt-3 text-[13px] text-ink-faint" data-id="element-147" data-mp="26hvx5pn6j4xv">{block.caption}</p>}
        </>;
    case 'steps':
      return <>
          {block.heading && <Heading data-id="element-148" data-mp="2hz28p06j2u90">{block.heading}</Heading>}
          <ol className={`border-t border-rule ${block.heading ? 'mt-5' : ''}`} data-id="element-149" data-mp="2i0daiugexrxh">
            {block.steps.map(step => <li key={step.label} className="flex flex-col gap-1 border-b border-rule py-5 sm:flex-row sm:gap-6" data-id="element-150" data-mp="2d2bt0dymy0o4">
                <span className="shrink-0 text-[13px] tabular-nums text-ink-faint sm:w-10 sm:pt-1" data-id="element-151" data-mp="241dlvmswhd1b">
                  {step.label}
                </span>
                <div className="min-w-0" data-id="element-152" data-mp="1u9rddy7y5s29">
                  <p className="text-[17px] font-medium text-ink" data-id="element-153" data-mp="1uasno0h52aix">{step.title}</p>
                  {step.body && <p className="mt-2 text-[16px] leading-relaxed text-ink-muted" data-id="element-154" data-mp="1pcr4zgnmkd7z">
                      <Rich text={step.body} data-id="element-155" data-mp="1kbbntzokiquv" />
                    </p>}
                </div>
              </li>)}
          </ol>
          {block.caption && <p className="mt-4 text-[13px] leading-relaxed text-ink-faint" data-id="element-156" data-mp="3rwtj2w6vdoy8">{block.caption}</p>}
        </>;
    case 'frames':
      return <>
          {block.heading && <Heading data-id="element-157" data-mp="4tx68dzycavf">{block.heading}</Heading>}
          <div className={block.heading ? 'mt-5' : ''} data-id="element-158" data-mp="4qsy55jd8uzh">
            <FrameViewer frames={block.frames} data-id="element-159" data-mp="9qxdgs8jfjo4" />
          </div>
          {block.caption && <p className="mt-4 text-[13px] leading-relaxed text-ink-faint" data-id="element-160" data-mp="38anfd1qtz41c">{block.caption}</p>}
        </>;
    case 'model':
      return <>
          {block.heading && <Heading data-id="element-161" data-mp="3i7id3ifa50ek">{block.heading}</Heading>}
          <div className={block.heading ? 'mt-5' : ''} data-id="element-162" data-mp="3i6gyx52yvxsm">
            <ModelStage src={block.src} frames={block.frames} stages={block.stages} data-id="element-163" data-mp="3mys00b9g1pmr" />
          </div>
          {block.caption && <p className="mt-4 text-[13px] leading-relaxed text-ink-faint" data-id="element-164" data-mp="2piun59jl5muo">{block.caption}</p>}
        </>;
    case 'stats':
      return <>
          {block.heading && <Heading data-id="element-165" data-mp="1hgm6c2pv1cte">{block.heading}</Heading>}
          <dl className={`grid grid-cols-1 gap-8 sm:grid-cols-3 ${block.heading ? 'mt-7' : ''}`} data-id="element-166" data-mp="1mg8is9xv6p15">
            {block.stats.map(stat => <div key={stat.label} data-id="element-167" data-mp="1weekaqfrdt5h">
                <dt className="sr-only" data-id="element-168" data-mp="1weo94ap2hu9l">{stat.label}</dt>
                <dd data-id="element-169" data-mp="1wc24ojxh3uvl">
                  <span className="block text-[34px] font-medium leading-none tracking-tightest text-ink" data-id="element-170" data-mp="yr64nc2fvebe">
                    {stat.value}
                  </span>
                  <span className="mt-2.5 block text-[14px] leading-snug text-ink-muted" data-id="element-171" data-mp="13nmucapae0ql">
                    {stat.label}
                  </span>
                  {stat.note && <span className="mt-1 block text-[13px] leading-snug text-ink-faint" data-id="element-172" data-mp="1dgv8jf1x1wfd">
                      {stat.note}
                    </span>}
                </dd>
              </div>)}
          </dl>
          {block.caption && <p className="mt-6 text-[13px] leading-relaxed text-ink-faint" data-id="element-173" data-mp="2y8hmcgarz8zy">{block.caption}</p>}
        </>;
    case 'callout':
      return <div className="border-l border-rule pl-6" data-id="element-174" data-mp="2p8ujdq2edv4s">
          {block.heading && <h2 className="text-[19px] font-medium leading-snug tracking-tight text-ink" data-id="element-175" data-mp="2paxir0pfx0m6">
              {block.heading}
            </h2>}
          <div className={`space-y-3 text-[16px] leading-relaxed text-ink-muted ${block.heading ? 'mt-3' : ''}`} data-id="element-176" data-mp="2fawud0cprub4">
            {block.body.map((paragraph, index) => <p key={index} data-id="element-177" data-mp="26j4rpbtxlph3" data-mpi={index}>
                <Rich text={paragraph} data-id="element-178" data-mp="26g8op9388als" />
              </p>)}
          </div>
        </div>;
    case 'demo':
      {
        const Demo = demos[block.name];
        if (!Demo) return null;
        return <>
          {block.heading && <Heading data-id="element-179" data-mp="1rkst2zn006na">{block.heading}</Heading>}
          <div className={block.heading ? 'mt-5' : ''} data-id="element-180" data-mp="1rluf1z5jydjs">
            <Demo data-id="element-181" data-mp="1hx70knsae9ri" />
          </div>
          {block.caption && <p className="mt-3 text-[13px] leading-relaxed text-ink-faint" data-id="element-182" data-mp="1mwjn17f8xetk">{block.caption}</p>}
        </>;
      }
    case 'embed':
      return <>
          {block.heading && <Heading data-id="element-183" data-mp="18z4rdkyegat5">{block.heading}</Heading>}
          <div className={`overflow-hidden border border-rule bg-surface ${block.heading ? 'mt-5' : ''}`} data-id="element-184" data-mp="1czexqyq3t38j">
            <iframe src={block.src} title={block.title} loading="lazy" className={`w-full ${aspects[block.aspect ?? '16/9']}`} data-id="element-185" data-mp="25n1cn34s7uap" />
          </div>
          {block.caption && <p className="mt-3 text-[13px] text-ink-faint" data-id="element-186" data-mp="1vw6hf99k5kd4">{block.caption}</p>}
        </>;
    case 'placeholder':
      return <div className={`${pending} ${aspects[block.aspect ?? '3/2']}`} data-id="element-187" data-mp="2jkzdd5q6js7q">
          <p className="text-[15px] font-medium text-ink-muted" data-id="element-188" data-mp="2oetuc1eeht34">{block.label}</p>
          {block.note && <p className="mt-2 text-[13px] leading-relaxed text-ink-faint" data-id="element-189" data-mp="cwgxo14ctv82">{block.note}</p>}
        </div>;
    default:
      return null;
  }
}
export function WorkDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const item = getWorkItem(slug);
  if (!item?.detail) {
    return <Navigate to="/work" replace data-id="element-190" data-mp="hrcztu3b1yyw" />;
  }
  const {
    detail
  } = item;
  return (
    // One reading column rather than a column plus a rail. The sidebar carried
    // metadata that competed with the writing for the reader's first look, and
    // on a piece that is mostly prose there is nothing for it to do.
    <main className="mx-auto w-full max-w-[48rem] px-6 py-10 md:px-8" data-id="element-191" data-mp="3visvgm3djof">
      <Link to="/work" className="group inline-flex items-center gap-2 text-[13px] text-ink-muted transition-colors duration-150 ease-expo hover:text-ink" data-id="element-192" data-mp="3v2ll8bdo1w08">
        <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-200 ease-expo group-hover:-translate-x-0.5" aria-hidden="true" data-id="element-193" data-mp="1acw7y2sm2i9n" />
        All work
      </Link>

      <motion.header initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease
      }} className="mt-20 md:mt-24" data-id="element-194" data-mp="3l2chj7ztl9wk">
        <p className="text-[13px] text-ink-faint" data-id="element-195" data-mp="3u296laq4gas5">
          {item.role} · {item.year}
        </p>
        <h1 className="mt-5 text-4xl font-medium leading-[1.06] tracking-tightest text-ink sm:text-5xl" data-id="element-196" data-mp="2whi0frzt67g">
          {item.title}
        </h1>
        <p className="mt-6 text-[20px] leading-snug text-ink-muted" data-id="element-197" data-mp="7sogxolz1hfw">{detail.tagline}</p>
      </motion.header>

      {detail.cover && <figure className="mt-12 overflow-hidden bg-surface" data-id="element-198" data-mp="gmuqcj8sqmxb">
          <img src={detail.cover.src} alt={detail.cover.alt} className={`aspect-[3/2] w-full object-cover ${detail.cover.tone === 'full' ? '' : 'grayscale'}`} data-id="element-199" data-mp="gp774t2tir8s" />
        </figure>}

      <article className="mt-16 border-t border-rule pt-16" data-id="element-200" data-mp="10q8rm6oenhfw">
        {detail.blocks.map((block, index) => <div key={index} className={index === 0 ? '' : 'mt-16'} data-id="element-201" data-mp="vpcr8339idf8" data-mpi={index}>
            <Block block={block} data-id="element-202" data-mp="qqhyo9fhtahx" />
          </div>)}
      </article>
    </main>
  );
}